import { mwGet, mwPost, unsupported } from "../mw/client";
import { epgDetail, mapEpg } from "../mw/live";
import { playVod } from "../mw/catalog";
import { finiteNonNegative, recordingDate, recordState } from "../../utils/recording";

const unwrapPayload = (response) => {
  const payload = response?.data;

  if (payload && typeof payload === "object") {
    if (payload.Data !== undefined) return payload.Data;
    if (payload.data !== undefined) return payload.data;
  }

  return payload;
};

const parseDateTimeValue = recordingDate;

const toIsoOrRaw = (value) => {
  if (!value) return null;
  const parsed = parseDateTimeValue(value);
  return parsed ? parsed.toISOString() : null;
};

const toDurationSeconds = (startTime, endTime, fallback = 0) => {
  const start = parseDateTimeValue(startTime)?.getTime();
  const end = parseDateTimeValue(endTime)?.getTime();
  if (
    start === null ||
    start === undefined ||
    end === null ||
    end === undefined ||
    Number.isNaN(start) ||
    Number.isNaN(end) ||
    end <= start
  ) {
    return fallback;
  }
  return Math.floor((end - start) / 1000);
};

const normalizeStatus = item => ({ READY: "1", RECORDING: "999", PROCESSING: "998", PENDING: "0", SCHEDULED: "0", FAILED: "-1", EXPIRED: "-3", UNKNOWN: "-4" }[recordState(item)]);

const normalizeRecordItem = (item) => {
  if (!item || typeof item !== "object") return item;

  if (item?.epgProgramId || item?.posters || item?.title) {
    const vodUId =
      item?.vodUId ||
      item?.VodUId ||
      item?.vodUid ||
      item?.VodUid ||
      item?.contentId ||
      item?.ContentId ||
      item?.contentUId ||
      item?.ContentUId ||
      null;

    return {
      ...item,
      epgProgramId: item?.epgProgramId || item?.EpgUId || item?.epgUId,
      vodUId,
      duration: finiteNonNegative(item.duration ?? item.Duration) ?? toDurationSeconds(item.startTime, item.endTime, null),
      startTime: toIsoOrRaw(item.startTime),
      endTime: toIsoOrRaw(item.endTime),
      newstatus: normalizeStatus(item),
      status: recordState(item)
    };
  }

  const epgProgramId = item?.EpgUId || item?.epgUId || item?.epgProgramId;
  const channelUId = item?.ChannelUId || item?.channelUId || item?.channelId;
  const title = item?.EpgTitle || item?.epgTitle || item?.title;
  const description =
    item?.EpgShortDescription || item?.epgShortDescription || item?.description;
  const thumbnail =
    item?.EpgThumbnailImageUrl ||
    item?.epgThumbnailImageUrl ||
    item?.thumbnailImageUrl;
  const startTime =
    item?.EpgStart || item?.epgStart || item?.startTime || item?.startDateTime;
  const endTime =
    item?.EpgEnd || item?.epgEnd || item?.endTime || item?.endDateTime;
  const duration =
    finiteNonNegative(item?.duration ?? item?.Duration) ??
    toDurationSeconds(startTime, endTime, null);
  const seriesId = item?.SeriesId || item?.seriesId || null;
  const vodUId =
    item?.VodUId ||
    item?.vodUId ||
    item?.vodUid ||
    item?.VodUid ||
    item?.ContentId ||
    item?.contentId ||
    item?.ContentUId ||
    item?.contentUId ||
    null;

  return {
    ...item,
    id: item?.UId || item?.uId || item?.id,
    npvrRecordUId: item?.UId || item?.uId || item?.npvrRecordUId,
    epgProgramId,
    channelUId,
    title,
    description,
    startTime: toIsoOrRaw(startTime),
    endTime: toIsoOrRaw(endTime),
    duration,
    seriesId,
    vodUId,
    isSerie: !!seriesId,
    newstatus: normalizeStatus(item),
    status: recordState(item),
    posters: thumbnail
      ? [
          {
            posterType: "HorizontalLow",
            posterUrl: thumbnail,
            type: "HorizontalHigh",
            url: thumbnail
          }
        ]
      : []
  };
};

const normalizeRecordListResponse = (response) => {
  const payload = unwrapPayload(response);
  if (!Array.isArray(payload) || payload.some(item => !item || typeof item !== "object")) throw new Error("Recording list unavailable");
  const list = payload;
  return {
    ...response,
    data: list.map(normalizeRecordItem)
  };
};

const normalizeSubscriberQuota = payload => {
  const rawCapacity = finiteNonNegative(payload?.ActiveDurationQuota ?? payload?.activeDurationQuota);
  const usedSeconds = finiteNonNegative(payload?.UsedDurationQuota ?? payload?.usedDurationQuota);
  const label = payload?.ActiveDurationQuotaLabel ?? payload?.activeDurationQuotaLabel;
  const parts = typeof label === "string" && /^\d+:[0-5]\d:[0-5]\d$/.test(label) ? label.split(":").map(Number) : null;
  const units = { seconds: 1, minutes: 60, hours: 3600 };
  const unit = String(payload?.durationQuotaUnit || "seconds").toLowerCase();
  const capacity = parts ? finiteNonNegative(parts[0] * 3600 + parts[1] * 60 + parts[2])
    : rawCapacity === null || !units[unit] ? null : finiteNonNegative(rawCapacity * units[unit]);
  if (usedSeconds === null) throw new Error("Recording usage unavailable");
  const isLimitless = payload?.IsDurationQuotaLimitless === true || payload?.isDurationQuotaLimitless === true;
  if (!isLimitless && capacity === null) throw new Error("Recording quota unavailable");
  const countLimit = finiteNonNegative(payload?.ActiveStorageQuota ?? payload?.activeStorageQuota);
  const usedCount = finiteNonNegative(payload?.UsedStorageQuota ?? payload?.usedStorageQuota);
  return {
    isLimitless,
    usedQuota: Math.floor(usedSeconds / 60),
    NpvrQuota: isLimitless ? null : capacity / 60,
    quotaLeft: isLimitless ? null : Math.max(0, capacity - usedSeconds) / 60,
    recordLimit: countLimit,
    usedRecords: usedCount,
    countLimitReached: payload?.IsStorageQuotaLimitless !== true && countLimit !== null && usedCount !== null && usedCount >= countLimit,
    source: "subscriber"
  };
};

const fetchQuotaFromSubscriber = async () =>
  normalizeSubscriberQuota((await mwGet("/subscriber")).data);

const checkNpvrStatus = async (id) => {
  const response = await mwGet("/npvr/records");
  const item = response.data.find((r) => r.EpgUId === id);
  return {
    ...response,
    data: {
      ...(item ? normalizeRecordItem(item) : {}),
      status: item ? normalizeStatus(item) : "-2"
    }
  };
};

const checkNpvrQuota = async () => ({
  status: 200,
  data: await fetchQuotaFromSubscriber()
});

const addRecord = async (id, seriesId, time, nextEpisodes, channelId) => {
  if (nextEpisodes) return unsupported("Otomatik dizi kaydı");
  if (!channelId) channelId = (await epgDetail(id)).data.ChannelUId;
  return mwPost("/npvr/record", { EpgUId: id, ChannelUId: channelId });
};

const removeRecord = async (id, all, recordId, channelId) => {
  if (all) return unsupported("Toplu dizi kaydı silme");
  if (!recordId) {
    const record = (await mwGet("/npvr/records")).data.find(
      (r) => r.EpgUId === id || r.UId === id
    );
    if (!record) throw new Error("Kayıt bulunamadı.");
    recordId = record.UId;
    channelId = record.ChannelUId;
    id = record.EpgUId;
  }
  return mwPost("/npvr/remove", {
    NpvrRecordUId: recordId,
    EpgUId: id,
    ChannelUId: channelId
  });
};

const getRecordList = async options => normalizeRecordListResponse(await mwGet("/npvr/records", undefined, options));

const getRecordedList = async (options) => {
  const response = await getRecordList(options);
  return {
    ...response,
    data: response.data.filter((r) => r.newstatus === "1")
  };
};

const getWillBeRecordedList = async (options) => {
  const response = await getRecordList(options);
  return {
    ...response,
    data: response.data.filter((r) => ["SCHEDULED", "PENDING", "RECORDING", "PROCESSING"].includes(r.status))
  };
};

const getUnavailableRecordList = async options => {
  const response = await getRecordList(options);
  return { ...response, data: response.data.filter(r => ["FAILED", "EXPIRED", "UNKNOWN"].includes(r.status)) };
};

const getSeriesDetail = () => unsupported("Dizi kayıt detayı");

const getNpvrWatchUrl = async (id) => {
  const records = await mwGet("/npvr/records");
  const record = records.data.find((r) => r.EpgUId === id || r.UId === id);
  if (!record?.HasVod || !record.VodUId)
    throw new Error("Kayıt yayını hazır değil.");
  const response = await playVod(record.VodUId);
  return { ...response, data: response.data.url };
};

const getUserRecords = async (id) => {
  const response = normalizeRecordListResponse(await mwGet("/npvr/records"));
  return {
    ...response,
    data: {
      returncode: "0",
      data: response.data.filter((r) => !id || r.epgProgramId === id)
    }
  };
};

const getNpvrQuotaLimit = checkNpvrQuota;

const getUserSerieRecords = () => unsupported("Dizi kayıt listesi");

const startNpvr = (id, channelId) =>
  addRecord(id, null, null, false, channelId);

const updateNpvr = () => unsupported("Kayıt otomatik silme ayarı");

const checkNpvrLimit = async () => {
  const r = await checkNpvrQuota();
  return {
    ...r,
    data: {
      ...r.data,
      returncode: "0",
      islimitnpvr: !r.data.countLimitReached && (r.data.isLimitless || r.data.quotaLeft > 0) ? "0" : "2"
    }
  };
};

const startSerieNpvr = () => unsupported("Otomatik dizi kaydı");

const getNpvrPlayUrl = async (id) => {
  const response = await getNpvrWatchUrl(id);
  return {
    ...response,
    data: { returncode: "0", httpsplayurl: response.data }
  };
};

const getProgramPlayUrl = async (id) => {
  const epg = await epgDetail(id);
  if (!epg.data.VodUId) throw new Error("Tekrar izleme yayını bulunamadı.");
  const response = await playVod(epg.data.VodUId);
  return {
    ...response,
    data: { returncode: "0", httpsplayurl: response.data.url }
  };
};

const getRecordDetail = async (id) => {
  const response = await epgDetail(id);
  return {
    ...response,
    data: { returncode: "0", data: [mapEpg(response.data)] }
  };
};

const deleteNpvrRecord = (id) => removeRecord(id, false);

const deleteSerieNpvrRecord = () => unsupported("Toplu dizi kaydı silme");

export {
  checkNpvrStatus,
  checkNpvrQuota,
  addRecord,
  removeRecord,
  getRecordList,
  getRecordedList,
  getWillBeRecordedList,
  getUnavailableRecordList,
  getSeriesDetail,
  getNpvrWatchUrl,
  getUserRecords,
  getNpvrPlayUrl,
  getRecordDetail,
  deleteNpvrRecord,
  startNpvr,
  startSerieNpvr,
  getUserSerieRecords,
  deleteSerieNpvrRecord,
  getNpvrQuotaLimit,
  getProgramPlayUrl,
  updateNpvr,
  checkNpvrLimit
};
